import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import type { Edge, Node, OnMove, ReactFlowInstance } from '@xyflow/react';
import {
  FlowchartKeyboardActionKind,
  getFlowchartKeyboardAction,
  type FlowchartKeyboardCommandRunnerByKind,
} from './flowchart-keyboard';
import type { FlowchartDirectionKey } from './flowchart-interactions';
import type { FlowchartEdgeData, FlowchartNodeData } from './flowchart-types';
import type { LayoutResult } from './layout-flowchart';

const FIT_VIEW_PADDING = 0.32;
const FIT_VIEW_DURATION = 460;
const ZOOM_BUTTON_DURATION = 180;
const ZOOM_SLIDER_DURATION = 120;

interface UseFlowchartControlsInput {
  focusedNodeId: string | null;
  focusNodeNeighborhood: (nodeId: string) => void;
  handleDirectionalNavigation: (direction: FlowchartDirectionKey) => void;
  layout: LayoutResult | null;
  reactFlowInstance: ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>> | null;
  setFocusedNodeId: (nodeId: string | null) => void;
  setReactFlowInstance: (
    instance: ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>>,
  ) => void;
}

interface UseFlowchartControlsResult {
  handleFocusFlowchart: () => void;
  handleKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  handleReactFlowInit: (
    instance: ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>>,
  ) => void;
  handleReactFlowMove: OnMove;
  handleToggleMinimap: () => void;
  handleZoomChange: (targetZoom: number) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  isMinimapVisible: boolean;
  zoomLevel: number;
}

export function useFlowchartControls({
  focusedNodeId,
  focusNodeNeighborhood,
  handleDirectionalNavigation,
  layout,
  reactFlowInstance,
  setFocusedNodeId,
  setReactFlowInstance,
}: UseFlowchartControlsInput): UseFlowchartControlsResult {
  const [isMinimapVisible, setIsMinimapVisible] = useState<boolean>(true);
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  const handleFocusFlowchart = useCallback(() => {
    if (!reactFlowInstance) {
      return;
    }

    reactFlowInstance.fitView({
      duration: FIT_VIEW_DURATION,
      padding: FIT_VIEW_PADDING,
    });
  }, [reactFlowInstance]);

  const handleZoomIn = useCallback(() => {
    if (!reactFlowInstance) {
      return;
    }

    reactFlowInstance.zoomIn({ duration: ZOOM_BUTTON_DURATION });
  }, [reactFlowInstance]);

  const handleZoomOut = useCallback(() => {
    if (!reactFlowInstance) {
      return;
    }

    reactFlowInstance.zoomOut({ duration: ZOOM_BUTTON_DURATION });
  }, [reactFlowInstance]);

  const handleZoomChange = useCallback((targetZoom: number) => {
    if (!reactFlowInstance) {
      return;
    }

    setZoomLevel(targetZoom);
    reactFlowInstance.zoomTo(targetZoom, { duration: ZOOM_SLIDER_DURATION });
  }, [reactFlowInstance]);

  const handleToggleMinimap = useCallback(() => {
    setIsMinimapVisible((previousVisibility) => !previousVisibility);
  }, []);

  const handleReactFlowInit = useCallback((
    instance: ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>>,
  ): void => {
    setReactFlowInstance(instance);
    setZoomLevel(instance.getZoom());
  }, [setReactFlowInstance]);

  const handleReactFlowMove = useCallback<OnMove>((_event, viewport) => {
    setZoomLevel(viewport.zoom);
  }, []);

  const keyboardCommandRunnerByKind = useMemo<FlowchartKeyboardCommandRunnerByKind>(() => {
    return {
      [FlowchartKeyboardActionKind.ZoomIn]: handleZoomIn,
      [FlowchartKeyboardActionKind.ZoomOut]: handleZoomOut,
      [FlowchartKeyboardActionKind.ClearFocus]: () => setFocusedNodeId(null),
      [FlowchartKeyboardActionKind.RefocusNode]: () => {
        if (!focusedNodeId) {
          return;
        }

        focusNodeNeighborhood(focusedNodeId);
      },
    };
  }, [focusNodeNeighborhood, focusedNodeId, handleZoomIn, handleZoomOut, setFocusedNodeId]);

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (!layout) {
      return;
    }

    const keyboardAction = getFlowchartKeyboardAction(event.key);
    if (keyboardAction.kind === FlowchartKeyboardActionKind.None) {
      return;
    }

    event.preventDefault();
    if (keyboardAction.kind === FlowchartKeyboardActionKind.Directional) {
      handleDirectionalNavigation(keyboardAction.direction);
      return;
    }

    keyboardCommandRunnerByKind[keyboardAction.kind]();
  }, [handleDirectionalNavigation, keyboardCommandRunnerByKind, layout]);

  return {
    handleFocusFlowchart,
    handleKeyDown,
    handleReactFlowInit,
    handleReactFlowMove,
    handleToggleMinimap,
    handleZoomChange,
    handleZoomIn,
    handleZoomOut,
    isMinimapVisible,
    zoomLevel,
  };
}
